import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import { compact } from 'lodash-es';
import { Controller, useForm } from 'react-hook-form';
import { nameValidation } from 'validators';
import { FunctionalLoader, TextField, ConfirmationModal, Grid, GridItem, MenuItem, Select, Typography } from 'seagull';
import { validateContact } from '~/business-logic/validators';
import { ResponsiveModal } from '~/client-flow/components/ResponsiveModal';
import { useResponsive } from '~/client-flow/contexts';
import { ErrorText } from '~/constants';
import { useEventReporter, useOverlayContext, useStore, useStoreActions } from '~/contexts';
import { useOnMount } from '~/hooks/useOnMount';
import { useLocaleKeys } from '~/i18n';
import { ErrorResponse, ServerError } from '~/infra/ErrorResponse';
import { handlePromise } from '~/infra/utils';
import { UserRole } from '~/models';
import { selectors } from '~/store/state-selectors';
import { connectControllerToTextField } from '~/utils/react-hook-form.utils';
import styles from './InviteOrEditMemberModal.module.scss';
const testPrefix = 'invite-member-modal';
const defaultRole = UserRole.Member;
export default function InviteOrEditMemberModal(props) {
    const { isResponsive, member, allMembers, onClose, onSuccess, skin = 'primary' } = props;
    const overlayContext = useOverlayContext();
    const worksAt = useStore(selectors.user.business);
    const userRole = useStore(selectors.user.role);
    const isVendor = useStore(selectors.user.isVendor);
    const userId = useStore(selectors.user.id);
    const { setWorksAt, api, logBI, fetchUserInfo } = useStoreActions();
    const { reportFormSubmission, prepareFormObject } = useEventReporter();
    const isEditMode = !!member;
    const { control, formState, handleSubmit, reset } = useForm({
        mode: 'all',
        defaultValues: isEditMode ? {
            firstName: member.firstName(),
            lastName: member.lastName(),
            email: member.email(),
            role: member.effectiveRole()
        } : {
            firstName: '',
            lastName: '',
            email: '',
            role: ''
        }
    });
    const { isDesktop } = useResponsive();
    const { t: texts } = useLocaleKeys('components.inviteOrEditMemberModal');
    const { t: commonTexts } = useLocaleKeys('common');
    const [roleDropdownEl, setRoleDropdownEl] = useState(null);
    const [existingMembers, setExistingMembers] = useState(allMembers);
    const firstNameValidator = useMemo(()=>nameValidation('First name', {
            required: texts.firstNameRequired()
        }), []);
    const lastNameValidator = useMemo(()=>nameValidation('Last name', {
            required: texts.lastNameRequired()
        }), []);
    const roleValidator = useMemo(()=>nameValidation('Role', {
            required: texts.roleRequired()
        }), []);
    useOnMount(()=>{
        async function retrieveMembers() {
            try {
                const { members } = await api.member.getAll();
                setExistingMembers(members);
            } catch (err) {
                overlayContext.showErrorToast(ErrorText.UnexpectedErrorTryAgain, 'failed-to-load-members-toast');
                onClose();
            }
        }
        if (!existingMembers) {
            // relevant in the client onboarding and payment method tab
            retrieveMembers();
        }
    });
    const onValidSubmit = async (param)=>{
        let { firstName, lastName, email, role } = param;
        if (isEditMode) {
            const isEditingCurrentUser = member.id() === userId;
            const payload = {
                id: member.id(),
                currentRole: member.effectiveRole(),
                newRole: role || defaultRole
            };
            logBI(12200, _object_spread_props(_object_spread({}, payload), {
                email: member.email()
            }));
            const changeRoleLoader = 'changeRole';
            overlayContext.startLoader(changeRoleLoader);
            const result = await handlePromise(api.member.changeRole(payload));
            if (!result.success) {
                overlayContext.stopLoader(changeRoleLoader);
                return overlayContext.showErrorToast(commonTexts.toasts.failedToChangeRole(), 'cr-fail');
            }
            if (isEditingCurrentUser) {
                await handlePromise(fetchUserInfo());
            }
            const userChangedHimselfToMember = isEditingCurrentUser && role === UserRole.Member;
            if (!userChangedHimselfToMember) {
                // When a user changes his own role to member he's no longer allowed to access member APIs (which is what onSuccess does).
                onSuccess && await onSuccess();
            }
            overlayContext.stopLoader(changeRoleLoader);
            onClose();
        } else {
            try {
                const invitedRole = role || defaultRole;
                logBI(12201, {
                    email,
                    role: invitedRole
                });
                await api.member.invite({
                    firstName,
                    lastName,
                    email,
                    role: invitedRole
                });
                if (worksAt) {
                    setWorksAt(_object_spread_props(_object_spread({}, worksAt), {
                        membersCount: worksAt.membersCount + 1
                    }));
                }
                reportFormSubmission('team-invited-member', [
                    prepareFormObject('email', email),
                    prepareFormObject('firstName', firstName),
                    prepareFormObject('lastName', lastName)
                ]);
                onSuccess && await onSuccess();
                overlayContext.showSuccessToast(texts.inviteSent({
                    email
                }), 'sent-invite-succsessfully-toast');
                reset();
            } catch (err) {
                const e = new ErrorResponse(err);
                const text = e.hasServerError(ServerError.PendingInvitationAlreadyExists) ? texts.pendingInvitationError() : e.hasServerError([
                    ServerError.UserAlreadyAffiliatedWithBusiness,
                    ServerError.UserAlreadyInBusiness
                ]) ? texts.alreadyMemberError() : ErrorText.UnexpectedErrorTryAgain;
                overlayContext.showErrorToast(text);
            }
        }
    };
    const emailRules = ()=>({
            validate: {
                email: validateContact.email,
                emailAlreadyUsed: (email)=>(existingMembers === null || existingMembers === void 0 ? void 0 : existingMembers.every((m)=>m.email() !== email)) || texts.alreadyMemberError()
            }
        });
    const { t: roleTexts } = useLocaleKeys('components.inviteOrEditMemberModal.roles');
    const roleOptions = compact([
        {
            value: UserRole.Owner,
            text: roleTexts.ownerText(),
            subtext: roleTexts.adminSubtext(),
            disabled: userRole !== UserRole.Owner && userRole !== UserRole.AnchorAdmin
        },
        isVendor && {
            value: UserRole.Admin,
            text: roleTexts.adminText(),
            subtext: roleTexts.adminLimitedSubtext()
        },
        {
            value: UserRole.Member,
            text: roleTexts.memberText(),
            subtext: isVendor ? texts.memberVendorSubtext() : texts.memberNonVendorSubtext()
        }
    ]);
    const commonModalProps = {
        skin,
        testId: testPrefix,
        title: texts.modalTitle({
            mode: isEditMode ? 'edit' : 'other'
        }),
        onClose,
        primaryButtonLabel: isEditMode ? commonTexts.buttons.save() : commonTexts.buttons.sendInvitation(),
        primaryButtonDisabled: formState.isSubmitting || isEditMode && !formState.isDirty,
        onPrimaryButtonClick: handleSubmit(onValidSubmit),
        secondaryButtonLabel: commonTexts.buttons.close(),
        onSecondaryButtonClick: onClose
    };
    const modalContent = /*#__PURE__*/ _jsxs(Grid, {
        className: styles.modalContent,
        children: [
            /*#__PURE__*/ _jsx(GridItem, {
                span: isDesktop ? 6 : 12,
                children: /*#__PURE__*/ _jsx(Controller, {
                    control: control,
                    name: "firstName",
                    rules: firstNameValidator,
                    render: (renderProps)=>/*#__PURE__*/ _jsx(TextField, _object_spread({
                            collapseHelperText: false,
                            disabled: isEditMode,
                            label: texts.firstName(),
                            testId: "".concat(testPrefix, "-input-first-name"),
                            bi: {
                                name: 'first_name'
                            }
                        }, connectControllerToTextField(renderProps), firstNameValidator.dataAttr))
                })
            }),
            /*#__PURE__*/ _jsx(GridItem, {
                span: isDesktop ? 6 : 12,
                children: /*#__PURE__*/ _jsx(Controller, {
                    control: control,
                    name: "lastName",
                    rules: lastNameValidator,
                    render: (renderProps)=>/*#__PURE__*/ _jsx(TextField, _object_spread({
                            collapseHelperText: false,
                            disabled: isEditMode,
                            label: texts.lastName(),
                            testId: "".concat(testPrefix, "-input-last-name"),
                            bi: {
                                name: 'last_name'
                            }
                        }, connectControllerToTextField(renderProps), lastNameValidator.dataAttr))
                })
            }),
            /*#__PURE__*/ _jsx(GridItem, {
                span: 12,
                children: /*#__PURE__*/ _jsx(Controller, {
                    control: control,
                    name: "email",
                    rules: isEditMode ? undefined : emailRules(),
                    render: (renderProps)=>/*#__PURE__*/ _jsx(TextField, _object_spread({
                            collapseHelperText: false,
                            disabled: isEditMode,
                            label: texts.email(),
                            testId: "".concat(testPrefix, "-input-email"),
                            bi: {
                                name: 'email'
                            }
                        }, connectControllerToTextField(renderProps)))
                })
            }),
            /*#__PURE__*/ _jsx(GridItem, {
                span: 12,
                children: /*#__PURE__*/ _jsx(Controller, {
                    control: control,
                    name: "role",
                    rules: roleValidator,
                    render: (p)=>/*#__PURE__*/ _jsx(Select, _object_spread_props(_object_spread({
                            collapseHelperText: false,
                            label: texts.role(),
                            ref: setRoleDropdownEl,
                            testId: "".concat(testPrefix, "-input-role"),
                            placeholder: texts.selectRole(),
                            bi: {
                                name: 'role'
                            }
                        }, connectControllerToTextField(p)), {
                            children: roleOptions.map((param)=>{
                                let { value, text, subtext, disabled } = param;
                                return /*#__PURE__*/ _jsxs(MenuItem, {
                                    variant: "multiline",
                                    value: value,
                                    disabled: disabled,
                                    label: text,
                                    testId: "".concat(testPrefix, "-role-opt-").concat(value),
                                    style: {
                                        width: roleDropdownEl === null || roleDropdownEl === void 0 ? void 0 : roleDropdownEl.clientWidth
                                    },
                                    children: [
                                        /*#__PURE__*/ _jsx(Typography, {
                                            weight: "normal",
                                            children: text
                                        }),
                                        /*#__PURE__*/ _jsx(Typography, {
                                            children: subtext
                                        })
                                    ]
                                }, value);
                            })
                        }))
                })
            })
        ]
    });
    if (!existingMembers) {
        return /*#__PURE__*/ _jsx(FunctionalLoader, {
            show: true,
            name: "fetch-members-loader"
        });
    }
    return isResponsive ? /*#__PURE__*/ _jsx(ResponsiveModal, _object_spread_props(_object_spread({
        biName: "invite_or_edit_member_modal",
        isDesktop: isDesktop,
        backTitle: texts.backToAgreement(),
        className: isDesktop ? styles.whiteBackground : undefined,
        headerClassName: styles.whiteBackground
    }, commonModalProps), {
        children: modalContent
    })) : /*#__PURE__*/ _jsx(ConfirmationModal, _object_spread_props(_object_spread({}, commonModalProps), {
        className: styles.whiteBackground,
        headerClassName: styles.whiteBackground,
        textAlignCenter: false,
        content: modalContent,
        bi: {
            primaryButton: {
                name: 'send_invitation'
            },
            secondaryButton: {
                name: 'cancel'
            },
            closeButton: {
                name: 'close'
            }
        }
    }));
}
